import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { PolicyType } from '../types';
import {
  AiAlert,
  Alert,
  DasSenseHAlert,
  DaswatchAlert,
  DasWBGTAlert,
  DeviationAlert,
} from '../types/Alert';
import { Dasair } from '../types/Device';
import { WebSocketAlert, WebSocketAlertReceived } from '../types/Websocket';

import { getAreasOfProject } from '../apis/AreaApi';
import { getProjectDasairs } from '../apis/DasAirApi';
import { getProjectWorkers } from '../apis/DasloopApi';
import { getEquipmentById } from '../apis/DaspowerApi';
import { getProjectPlants } from '../apis/DastrackApi';
import { getProjectVehicles } from '../apis/DastrackVApi';
import { getProjectOfResources } from '../apis/ResourceApi';
import { useAppSelector } from '../hooks';

import {
  AlertType,
  DaswatchAlertType,
  getDasConcreteAlertType,
} from '../utils/getAlertIcon';
import { getDasairAlertType } from '../utils/getDasairType';
import { findTheClosestPipe } from '../utils/getWaterLevel';
import toAlertDescription from '../utils/toAlertDescription';

type AlertSnackbarPermissions = {
  workerReadable: boolean;
  plantReadable: boolean;
  equipmentReadable: boolean;
  vehicleReadable: boolean;
  areaReadable: boolean;
  dasairReadable: boolean;
  dasConcreteReadable: boolean;
  aiReadable: boolean;
};

type AlertSnackbarContext = {
  ws: WebSocketAlertReceived;
  projectId: string;
  t: any;
  permissions: AlertSnackbarPermissions;
};

type AlertSnackbarResult = {
  title: string;
  alertType: AlertType | DaswatchAlertType;
  areaName?: string;
  areaType?: string;
};

const handleDasloopAlert = async ({
  ws,
  projectId,
  t,
  permissions,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dasloop'>;
  let workerName: string | undefined;
  if (permissions.workerReadable && 'workerId' in ws.data) {
    workerName = await getProjectWorkers({
      projectId,
      params: { id: ws.data.workerId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title =
    dasAlert.alert.type === AlertType.CAP_OFF
      ? workerName
        ? `${workerName} (${dasAlert.dasId}) ${t(
            `alert-type.${dasAlert.alert.type}`,
          )}`
        : dasAlert.workerId
          ? `${t('no-permission')} (${dasAlert.dasId})`
          : `${t('no-assigned-worker')} (${dasAlert.dasId})`
      : workerName
        ? `${workerName} (${dasAlert.dasId})`
        : dasAlert.workerId
          ? `${t('no-permission')} (${dasAlert.dasId})`
          : `${t('no-assigned-worker')} (${dasAlert.dasId})`;
  return { title, alertType: dasAlert.alert.type };
};

const handleDassensesAlert = async ({
  ws,
  projectId,
  t,
  permissions,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as unknown as DasSenseHAlert;
  let workerName: string | undefined;
  if (permissions.workerReadable && 'workerId' in ws.data) {
    workerName = await getProjectWorkers({
      projectId,
      params: { id: ws.data.workerId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = workerName
    ? `${workerName} (${dasAlert.dasId})`
    : dasAlert.workerId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-worker')} (${dasAlert.dasId})`;
  return { title, alertType: dasAlert.alert.type };
};

const handleDasgasAlert = async ({
  ws,
  projectId,
  t,
  permissions,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dasgas'>;
  let workerName: string | undefined;
  if (permissions.workerReadable && 'workerId' in ws.data) {
    workerName = await getProjectWorkers({
      projectId,
      params: { id: ws.data.workerId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = workerName
    ? `${workerName} (${dasAlert.dasId})`
    : dasAlert.workerId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-worker')} (${dasAlert.dasId})`;
  return { title, alertType: dasAlert.alert.type };
};

const handleHeatstrokeAlert = async ({
  ws,
  projectId,
  t,
  permissions,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as unknown as DasSenseHAlert;
  let workerName: string | undefined;
  if (permissions.workerReadable && 'workerId' in ws.data) {
    workerName = await getProjectWorkers({
      projectId,
      params: { id: ws.data.workerId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = workerName
    ? `${workerName} (${dasAlert.dasId})`
    : dasAlert.workerId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-worker')} (${dasAlert.dasId})`;
  return { title, alertType: AlertType.DASSENSE_ALERT };
};

const handleDaswbgtAlert = async ({
  ws,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as unknown as DasWBGTAlert;
  const envName = dasAlert.resourceId?.name;
  const title = envName
    ? `${envName} (${dasAlert.dasId})`
    : `${dasAlert.dasId}`;
  return { title, alertType: AlertType.DASWBGT_HEATSTROKE };
};

const handleDaswatchAlert = async ({
  ws,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as DaswatchAlert;
  const title = `${ws.data.dasId} ${dasAlert.field}`;
  return { title, alertType: dasAlert.field as DaswatchAlertType };
};

const handleDastrackAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dastrack'>;
  let plantName: string | undefined;
  if (permissions.plantReadable && 'plantId' in ws.data) {
    plantName = await getProjectPlants({
      projectId,
      params: { id: ws.data.plantId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = plantName
    ? `${plantName} (${dasAlert.dasId})`
    : dasAlert.plantId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-plant')} (${dasAlert.dasId})`;
  return { title, alertType: dasAlert.alertType };
};

const handleDasRPAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dasrp'>;
  let plantName: string | undefined;
  if (permissions.plantReadable && 'plantId' in ws.data) {
    plantName = await getProjectPlants({
      projectId,
      params: { id: ws.data.plantId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = plantName
    ? `${plantName} (${dasAlert.dasId})`
    : dasAlert.plantId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-plant')} (${dasAlert.dasId})`;
  return { title, alertType: AlertType.DASRP };
};

const handleDascasGAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dascas'>;
  let plantName: string | undefined;
  if (permissions.plantReadable) {
    plantName = await getProjectPlants({
      projectId,
      params: { id: dasAlert.plantId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = plantName
    ? `${plantName} (${dasAlert.dasId})`
    : dasAlert.plantId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-plant')} (${dasAlert.dasId})`;
  return { title, alertType: dasAlert.alertType };
};

const handleDastempAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dastemp'>;
  let equipmentName: string | undefined;
  if (permissions.equipmentReadable) {
    equipmentName = await getEquipmentById(
      projectId,
      dasAlert.equipment?.id ?? '',
    ).then((res) => res.data.data.name);
  }
  const title = equipmentName
    ? `${equipmentName} (${dasAlert.dasId})`
    : dasAlert.equipment?.id
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-equipment')} (${dasAlert.dasId})`;
  return { title, alertType: AlertType.AREA_ALERT };
};

const handleDaspowerAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'daspower'>;
  let equipmentName: string | undefined;
  if (permissions.equipmentReadable) {
    equipmentName = await getEquipmentById(
      projectId,
      dasAlert.equipment?.id ?? '',
    ).then((res) => res.data.data.name);
  }
  const title = equipmentName
    ? `${equipmentName} (${dasAlert.dasId})`
    : dasAlert.equipment?.id
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-equipment')} (${dasAlert.dasId})`;
  return { title, alertType: AlertType.DASPOWER_ALERT };
};

const handleDaslockAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'daslock'>;
  let equipmentName: string | undefined;
  if (permissions.equipmentReadable) {
    equipmentName = await getEquipmentById(
      projectId,
      dasAlert.equipment?.id ?? '',
    ).then((res) => res.data.data.name);
  }
  const title = equipmentName
    ? `${equipmentName} (${dasAlert.dasId})`
    : dasAlert.equipment?.id
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-equipment')} (${dasAlert.dasId})`;
  return { title, alertType: dasAlert.alert.type };
};

const handleDeviationAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const deviationAlert = ws.data as Alert<'deviation'>;
  let resourceName: string | undefined;
  let areaName: string | undefined;
  let areaType: string | undefined;
  if (permissions.vehicleReadable && 'vehicleId' in ws.data) {
    resourceName = await getProjectVehicles({
      projectId,
      params: { id: ws.data.vehicleId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  if (permissions.areaReadable && 'areaId' in ws.data) {
    const res = await getAreasOfProject(projectId, {
      id: ws.data.areaId ?? undefined,
      selectVisible: false,
    });
    areaName = res.data.data?.[0]?.name;
    areaType = res.data.data?.[0]?.type;
  }
  const title = `${resourceName} ${t('leave')} ${
    areaName
      ? `${areaName} (${deviationAlert.dasId})`
      : `${t('no-permission')} (${deviationAlert.dasId})`
  }`;
  return {
    title,
    alertType: AlertType.DASTRACK_V_LEAVE_ALERT,
    areaName,
    areaType,
  };
};

const handleDasairAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dasair'>;
  let dasair: Dasair | undefined;
  if (permissions.dasairReadable && 'endpointId' in ws.data) {
    dasair = await getProjectDasairs({
      projectId,
      params: { id: ws.data.endpointId ?? undefined },
    }).then((res) => res.data.data?.[0]);
  }
  const title =
    dasair?.dasId ??
    (dasAlert.endpointId
      ? `${t('no-permission')} (${dasair?.dasId})`
      : `${t('deleted')} (${dasair?.dasId})`);
  return { title, alertType: getDasairAlertType(dasAlert) };
};

const handleAreaAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'area'>;
  let resourceName: string | undefined;
  let areaName: string | undefined;
  let areaType: string | undefined;
  if (permissions.workerReadable && 'workerId' in ws.data) {
    resourceName = await getProjectWorkers({
      projectId,
      params: { id: ws.data.workerId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  if (permissions.areaReadable && 'areaId' in ws.data) {
    const res = await getAreasOfProject(projectId, {
      id: ws.data.areaId ?? undefined,
      selectVisible: false,
    });
    areaName = res.data.data?.[0]?.name;
    areaType = res.data.data?.[0]?.type;
  }
  const workerName = resourceName;
  const title = workerName
    ? `${workerName} (${dasAlert.dasId})`
    : dasAlert.areaId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('deleted')} (${dasAlert.dasId})`;
  return { title, alertType: AlertType.AREA_ALERT, areaName, areaType };
};

const handleDasconcreteAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'dasconcrete'>;
  let structureName: string | undefined;
  if (permissions.dasConcreteReadable && 'resourceId' in ws.data) {
    structureName = await getProjectOfResources({
      projectId,
      resourceType: 'field',
      params: { id: ws.data.resourceId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = structureName
    ? `${structureName} (${dasAlert.dasId})`
    : dasAlert.resourceId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-structure')} (${dasAlert.dasId})`;
  return { title, alertType: getDasConcreteAlertType(dasAlert) };
};

const handleAiAlert = async ({
  ws,
  projectId,
  permissions,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as AiAlert;
  let structureName: string | undefined;
  if (permissions.aiReadable && 'resourceId' in ws.data) {
    structureName = await getProjectOfResources({
      projectId,
      resourceType: 'ai',
      params: { id: ws.data.resourceId ?? undefined },
    }).then((res) => res.data.data?.[0]?.name);
  }
  const title = structureName
    ? `${structureName} (${dasAlert.dasId})`
    : `${t('no-permission')} ${t('or')} ${t(
        'no-assigned-structure',
      )} (${dasAlert.dasId})`;
  return { title, alertType: dasAlert.field };
};

const handleDaswaterAlert = async ({
  ws,
  t,
}: AlertSnackbarContext): Promise<AlertSnackbarResult> => {
  const dasAlert = ws.data as Alert<'daswater'>;
  const pipeName = findTheClosestPipe(ws.pipes, dasAlert)?.name;
  const title = pipeName
    ? `${pipeName} (${dasAlert.dasId})`
    : dasAlert.resourceId
      ? `${t('no-permission')} (${dasAlert.dasId})`
      : `${t('no-assigned-pipe')} (${dasAlert.dasId})`;

  let alertType: AlertType;
  if (dasAlert.field === 'battery') {
    alertType = AlertType.DASWATER_L_LOWBATTERYALARM;
  } else if (dasAlert.field === 'distance') {
    alertType = AlertType.DASWATER_L_DISTANCE;
  } else if (dasAlert.field === 'isTilt') {
    alertType = AlertType.DASWATER_L_TILT;
  } else {
    alertType = AlertType.DASWATER_L;
  }
  return { title, alertType };
};

const alertSnackbarHandlers: Partial<
  Record<
    WebSocketAlertReceived['type'],
    (ctx: AlertSnackbarContext) => Promise<AlertSnackbarResult>
  >
> = {
  dasloop: handleDasloopAlert,
  dassenses: handleDassensesAlert,
  dasgas: handleDasgasAlert,
  heatstroke: handleHeatstrokeAlert,
  daswbgt: handleDaswbgtAlert,
  daswatch: handleDaswatchAlert,
  dastrack: handleDastrackAlert,
  dasrp: handleDasRPAlert,
  dascas_g: handleDascasGAlert,
  dastemp: handleDastempAlert,
  daspower: handleDaspowerAlert,
  daslock: handleDaslockAlert,
  deviation: handleDeviationAlert,
  dasair: handleDasairAlert,
  area: handleAreaAlert,
  dasconcrete: handleDasconcreteAlert,
  ai: handleAiAlert,
  daswater: handleDaswaterAlert,
};

const useAlertSnackBar = (projectId: string, websocket: WebSocketAlert) => {
  const projectRole = useAppSelector((store) => store.projects.role);
  const policies = useAppSelector((store) => store.projects.policies);
  const policyMap = new Map<PolicyType, boolean>();
  policies.forEach((policy) => {
    policyMap.set(policy.name, true);
  });
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const workerReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneWorkerGroup');

  const plantReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOnePlantGroup');

  const equipmentReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneEquipmentGroup');

  const vehicleReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneVehicleGroup');

  const areaReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadArea');

  const dasairReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadDasair');

  const dasConcreteReadable =
    projectRole === 'owner' || projectRole === 'admin';

  const aiReadable =
    projectRole === 'owner' ||
    projectRole === 'admin' ||
    policyMap.has('ReadOneAiGroup');

  useEffect(() => {
    const showSnackbar = async (ws: WebSocketAlertReceived) => {
      const permissions: AlertSnackbarPermissions = {
        workerReadable,
        plantReadable,
        equipmentReadable,
        vehicleReadable,
        areaReadable,
        dasairReadable,
        dasConcreteReadable,
        aiReadable,
      };

      const result = await alertSnackbarHandlers[ws.type]?.({
        ws,
        projectId,
        t,
        permissions,
      });

      const title = result?.title ?? '';
      const alertType = result?.alertType ?? AlertType.AREA_ALERT;

      const type = 'notify';

      let timestamp: string;
      if (ws.type === 'deviation') {
        timestamp = (ws.data as Alert<'deviation'>).exitedAt;
      } else {
        timestamp = (
          ws.data as Exclude<WebSocketAlertReceived['data'], DeviationAlert>
        ).timestamp;
      }

      const { description, descriptionNode } = toAlertDescription(
        ws.type,
        ws.data,
        t,
        type,
        {
          areaName:
            ws.type === 'area'
              ? (result?.areaName ??
                `${t('no-permission')} ${t('or')} ${t('deleted')}`)
              : undefined,
          areaType: result?.areaType,
          pipes: ws.pipes,
        },
      );

      enqueueSnackbar('', {
        variant: 'alertNotification',
        key: `alert-notification-${ws.data.id}`,
        data: {
          alertType,
          title,
          description,
          descriptionNode,
          timestamp,
        },
      });
    };
    if (websocket) {
      showSnackbar(websocket);
    }
  }, [projectId, websocket, t]);
};

export default useAlertSnackBar;
